import { FirestoreTool } from './aiAgent/tools/FirestoreTool'
import { log } from '@reusables/logger'
import store from '../store'
import { functions } from '@root/firebaseDatabase'
import { httpsCallable } from 'firebase/functions'

/**
 * AIAgentService - Main service interface for the AI agent system
 * Uses actual AI (Gemini) to understand queries and select tools
 */
class AIAgentService {
	constructor() {
		this.tools = new Map()
		this.conversationHistory = []
		this.maxHistoryLength = 20
		this.initialized = false
		this.currentContext = {
			lastUserId: null,
			lastDocumentId: null,
			lastCollection: null
		}
	}

	/**
	 * Initialize the AI agent with tools
	 */
	initialize() {
		if (this.initialized) {
			log('AI Agent already initialized')
			return
		}

		log('Initializing AI Agent Service...')

		try {
			// Register available tools
			this.registerTool(new FirestoreTool())

			this.initialized = true
			log('AI Agent Service initialized successfully')
		} catch (error) {
			log('Error initializing AI Agent Service:', error)
			throw error
		}
	}

	/**
	 * Process a user query using AI
	 * @param {string} query - User query
	 * @param {string} mode - Mode: 'help' or 'search'
	 * @returns {Promise<Object>} - Response object
	 */
	async query(query, mode = 'search') {
		if (!this.initialized) {
			this.initialize()
		}

		log('Processing query with AI:', query, 'Mode:', mode)

		// Get context from store
		const context = this.getContext()

		// Add user message to history
		this.addToHistory('user', query)

		try {
			// Call AI function to understand query and decide on tool usage
			const aiAgentQuery = httpsCallable(functions, 'aiAgentQuery')

			const contextToSend = {
				// NOTE: tenantId and userId are extracted from auth token server-side
				// for security. We only send conversation context here.
				currentUser: this.currentContext.lastUserId,
				currentDocument: this.currentContext.lastDocumentId,
				currentCollection: this.currentContext.lastCollection,
				mode: mode // 'help' or 'search'
			}


			const { data: aiResponse } = await aiAgentQuery({
				query,
				conversationHistory: this.getConversationHistory(),
				availableTools: this.getToolsMetadata(),
				context: contextToSend
			})


			// Check if the server already executed a tool and returned results
			if (aiResponse.toolResult) {

				// Server-side tool execution
				const toolResult = aiResponse.toolResult

				if (!toolResult.success) {
					const errorResponse = {
						success: false,
						message: `I tried to help, but encountered an error: ${toolResult.error}`,
						suggestions: ['Try rephrasing your request', 'Be more specific']
					}
					this.addToHistory('assistant', errorResponse.message)
					return errorResponse
				}

				// Format the response based on tool result
				const response = this.formatToolResponse(
					{ thinking: aiResponse.response },
					toolResult,
					aiResponse.extractField,
					query // Pass the original query for smart ranking
				)
				this.addToHistory('assistant', response.message)
				return response
			}

			// Legacy: If AI decided to use a tool (old flow - should not happen anymore)
			if (aiResponse.needsTool) {
				console.warn(
					'DEPRECATED: Client-side tool execution requested. This should not happen with new architecture.'
				)
				const tool = this.tools.get(aiResponse.tool)

				if (!tool) {
					throw new Error(`Tool ${aiResponse.tool} not found`)
				}

				// Execute the tool with AI-provided parameters
				const toolResult = await tool.run(aiResponse.parameters, context)

				if (!toolResult.success) {
					const errorResponse = {
						success: false,
						message: `I tried to ${aiResponse.thinking || 'help'}, but encountered an error: ${toolResult.error}`,
						suggestions: ['Try rephrasing your request', 'Be more specific']
					}
					this.addToHistory('assistant', errorResponse.message)
					return errorResponse
				}

				// Update context if we got a document
				if (toolResult.data && aiResponse.parameters.documentId) {
					this.currentContext.lastDocumentId = aiResponse.parameters.documentId
					this.currentContext.lastCollection = aiResponse.parameters.collection
					if (aiResponse.parameters.collection === 'users') {
						this.currentContext.lastUserId = aiResponse.parameters.documentId
					}
				}

				// Format the response based on tool result
				const response = this.formatToolResponse(aiResponse, toolResult, aiResponse.extractField)
				this.addToHistory('assistant', response.message)
				return response
			}

			// AI responded directly without needing a tool
			const response = {
				success: true,
				message: aiResponse.response,
				suggestions: aiResponse.suggestions || []
			}
			this.addToHistory('assistant', aiResponse.response)
			return response
		} catch (error) {
			console.error('Error in AI agent query:', error)
			console.error('Error details:', {
				message: error.message,
				stack: error.stack,
				code: error.code,
				fullError: error
			})
			const errorMessage = `I encountered an error: ${error.message || 'Unknown error'}`
			this.addToHistory('assistant', errorMessage)
			return {
				success: false,
				message: errorMessage,
				error: error.message,
				errorDetails: error
			}
		}
	}

	/**
	 * Format tool response for user display
	 * @param {Object} aiResponse - AI response with thinking
	 * @param {Object} toolResult - Tool execution result
	 * @param {string} extractField - Optional field to extract from result
	 * @param {string} originalQuery - Original user query for ranking
	 * @returns {Object}
	 */
	formatToolResponse(aiResponse, toolResult, extractField = null, originalQuery = null) {
		const toolType = toolResult.toolType || 'firestore' // Default to firestore for backwards compatibility

		// Format based on data type
		if (toolResult.count === 0) {
			const entityType = toolType === 'courses' ? 'courses' : 'results'
			return {
				success: false,
				message: `I searched but didn't find any ${entityType}. ${aiResponse.thinking || ''}`,
				suggestions: ['Try a different search', 'Check the spelling']
			}
		}

		// If user asked for a specific field, extract and return it
		if (extractField && toolResult.count === 1) {
			// toolResult.data might be an array with 1 element or a single object
			const item = Array.isArray(toolResult.data) ? toolResult.data[0] : toolResult.data

			// Extract field value (supports nested fields with dot notation)
			let fieldValue = this.getNestedField(item.data, extractField)

			// If not found in data, try top level
			if (fieldValue === undefined) {
				fieldValue = this.getNestedField(item, extractField)
			}

			if (fieldValue !== undefined) {
				// Format the value based on type
				const formattedValue = this.formatFieldValue(extractField, fieldValue)
				const isImage = this.isImageField(extractField, fieldValue)

				return {
					success: true,
					message: `The ${extractField} is:`,
					fieldData: {
						field: extractField,
						value: formattedValue,
						rawValue: fieldValue,
						isImage: isImage,
						isTimestamp: this.isFirebaseTimestamp(fieldValue)
					},
					data: { id: item.id }, // Only store minimal data
					suggestions: ['Show full document', 'Get another field']
				}
			} else {
				return {
					success: false,
					message: `I found the document, but it doesn't have a '${extractField}' field.`,
					data: { id: item.id }, // Only store minimal data
					suggestions: ['Show all fields', 'Try a different field']
				}
			}
		}

		if (toolResult.count === 1) {
			// toolResult.data might be an array with 1 element or a single object
			const item = Array.isArray(toolResult.data) ? toolResult.data[0] : toolResult.data
			const isCourse = toolType === 'courses'


			// Update context for single result
			if (item?.id) {
				this.currentContext.lastDocumentId = item.id
				this.currentContext.lastCollection = isCourse
					? 'coursesV2'
					: aiResponse.parameters?.collection || 'users'
				if (this.currentContext.lastCollection === 'users') {
					this.currentContext.lastUserId = item.id
				}
			}

			// Format based on tool type
			const formatted = isCourse ? this.formatCourseData(item) : this.formatUserData(item)

			// Extract essential data for storage
			const minimalData = {
				id: item?.id,
				type: formatted.type,
				...(item?.data?.name && { name: item.data.name }),
				...(item?.data?.path && { path: item.data.path })
			}

			return {
				success: true,
				message: 'I found this:',
				data: minimalData,
				formatted: formatted,
				suggestions: isCourse
					? ['View course content', 'Find another course']
					: ['Show more details', 'Find another']
			}
		}

		// Multiple results - rank them by relevance
		const rankedResults = this.rankSearchResults(
			toolResult.data,
			aiResponse.parameters,
			originalQuery
		)
		const isCourse = toolType === 'courses'

		return {
			success: true,
			message: `I found ${toolResult.count} ${isCourse ? 'courses' : 'results'}:`,
			data: rankedResults.map((item) => ({ id: item.id, type: isCourse ? 'course' : 'user' })),
			formatted: rankedResults.map((item) =>
				isCourse ? this.formatCourseData(item) : this.formatUserData(item)
			),
			suggestions: isCourse
				? ['Show first course', 'Include draft courses']
				: ['Show first result', 'Refine search']
		}
	}

	/**
	 * Rank and sort search results by relevance
	 * @param {Array} results - Array of user documents
	 * @param {Object} searchParams - Original search parameters
	 * @param {string} originalQuery - Original user query for smart ranking
	 * @returns {Array} - Sorted results
	 */
	rankSearchResults(results, searchParams, originalQuery = null) {
		if (!results || results.length === 0) return results

		// Extract name parts from original query if available
		let queryFirstName = null
		let queryLastName = null
		let queryEmail = null

		if (originalQuery) {
			const lowerQuery = originalQuery.toLowerCase()
			// Extract email if present
			const emailMatch = lowerQuery.match(/[\w.-]+@[\w.-]+\.\w+/)
			if (emailMatch) {
				queryEmail = emailMatch[0]
			}

			// Extract name parts (simple word extraction)
			// Look for patterns like "find michael palmer" or "michael palmer"
			const words = lowerQuery
				.replace(/find|search|show|get|user|named?/gi, '')
				.trim()
				.split(/\s+/)
				.filter((w) => w.length > 1 && !['in', 'the', 'system', 'for', 'me'].includes(w))

			if (words.length >= 2) {
				queryFirstName = words[0]
				queryLastName = words[1]
			} else if (words.length === 1) {
				queryFirstName = words[0]
			}
		}

		// Get search parameters from filter
		const whereFilters = searchParams?.filter?.where || {}
		const searchFirstName = whereFilters.firstName?.value?.toLowerCase() || queryFirstName
		const searchLastName = whereFilters.lastName?.value?.toLowerCase() || queryLastName
		const searchEmail = whereFilters.email?.value?.toLowerCase() || queryEmail


		return results
			.map((result) => {
				const data = result.data || {}
				const firstName = (data.firstName || '').toLowerCase()
				const lastName = (data.lastName || '').toLowerCase()
				const email = (data.email || '').toLowerCase()
				const displayName = (data.displayName || '').toLowerCase()

				let score = 0

				// Exact matches get highest priority
				if (searchFirstName && firstName === searchFirstName) score += 100
				if (searchLastName && lastName === searchLastName) score += 100
				if (searchEmail && email === searchEmail) score += 200

				// Partial matches get lower priority
				if (searchFirstName && firstName.includes(searchFirstName)) score += 50
				if (searchLastName && lastName.includes(searchLastName)) score += 50
				if (searchEmail && email.includes(searchEmail)) score += 100

				// Full name match in displayName
				if (searchFirstName && searchLastName) {
					const fullName = `${searchFirstName} ${searchLastName}`
					if (displayName.includes(fullName)) score += 150
				}

				return { ...result, _score: score }
			})
			.sort((a, b) => b._score - a._score)
	}

	/**
	 * Get nested field value using dot notation
	 * @param {Object} obj - Object to search
	 * @param {string} path - Dot notation path (e.g., "access.productId")
	 * @returns {*} - Field value or undefined
	 */
	getNestedField(obj, path) {
		if (!obj || !path) return undefined

		const keys = path.split('.')
		let current = obj

		for (const key of keys) {
			if (current && typeof current === 'object' && key in current) {
				current = current[key]
			} else {
				return undefined
			}
		}

		return current
	}

	/**
	 * Check if value is a Firebase Timestamp
	 * @param {*} value
	 * @returns {boolean}
	 */
	isFirebaseTimestamp(value) {
		return value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value
	}

	/**
	 * Check if field is an image field
	 * @param {string} fieldName
	 * @param {*} value
	 * @returns {boolean}
	 */
	isImageField(fieldName, value) {
		const imageFieldNames = ['image', 'photo', 'picture', 'avatar', 'cover', 'banner', 'thumbnail']
		const hasImageKeyword = imageFieldNames.some((keyword) =>
			fieldName.toLowerCase().includes(keyword)
		)

		const isImageUrl =
			typeof value === 'string' &&
			value.startsWith('http') &&
			(value.includes('image') ||
				value.match(/\.(jpg|jpeg|png|gif|webp|svg)/i) ||
				value.includes('cloudinary') ||
				value.includes('imagedelivery') ||
				value.includes('firebase'))

		return hasImageKeyword || isImageUrl
	}

	/**
	 * Format field value based on type
	 * @param {string} _fieldName - Field name (reserved for future use)
	 * @param {*} value
	 * @returns {string}
	 */
	formatFieldValue(_fieldName, value) {
		// Handle Firebase Timestamp
		if (this.isFirebaseTimestamp(value)) {
			const date = new Date(value.seconds * 1000)
			return date.toLocaleString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				timeZoneName: 'short'
			})
		}

		// Handle objects
		if (typeof value === 'object' && value !== null) {
			return JSON.stringify(value, null, 2)
		}

		// Handle other types
		return String(value)
	}

	/**
	 * Get display configuration for entity type
	 * @param {string} type - Entity type (user, course, event, etc.)
	 * @returns {Object} Display configuration
	 */
	getDisplayConfig(type) {
		const configs = {
			user: {
				entityName: 'user',
				entityNamePlural: 'users',
				primaryField: 'name',
				fields: [
					{ key: 'name', label: 'Name', primary: true },
					{ key: 'email', label: 'Email', secondary: true },
					{ key: 'id', label: 'ID', muted: true }
				],
				actions: [
					{ label: 'Select User', action: 'select', primary: true },
					{ label: 'Manage User', action: 'navigate', path: '/admin/users/{id}', icon: '👤' }
				]
			},
			course: {
				entityName: 'course',
				entityNamePlural: 'courses',
				primaryField: 'name',
				fields: [
					{ key: 'name', label: 'Name', primary: true },
					{ key: 'internalName', label: 'Internal Name', secondary: true },
					{ key: 'status', label: 'Status', muted: true, prefix: 'Status: ' },
					{ key: 'id', label: 'ID', muted: true }
				],
				actions: [
					{ label: 'Select Course', action: 'select', primary: true },
					{ label: 'Access Course', action: 'navigate', path: '/learning/{path}', icon: '📚' },
					{ label: 'Edit Course', action: 'navigate', path: '/admin/learning/{path}', icon: '✏️' }
				]
			}
			// Add more entity types here as needed: event, webinar, billing, analytics, etc.
		}

		return (
			configs[type] || {
				entityName: 'item',
				entityNamePlural: 'items',
				primaryField: 'name',
				fields: [
					{ key: 'name', label: 'Name', primary: true },
					{ key: 'id', label: 'ID', muted: true }
				]
			}
		)
	}

	/**
	 * Format entity data for display
	 * @param {Object} entity - Entity document
	 * @param {string} type - Entity type
	 * @returns {Object} Formatted data with display config
	 */
	formatEntityData(entity, type) {
		const data = entity.data || {}
		const config = this.getDisplayConfig(type)

		// Build formatted object with all fields from config
		const formatted = {
			id: entity.id,
			type: type,
			_displayConfig: config
		}

		// Extract values for each configured field
		config.fields.forEach((field) => {
			if (field.key === 'id') {
				// ID is already set from entity.id, don't overwrite
				return
			} else if (field.key === 'name' && type === 'user') {
				// Special handling for user names
				formatted.name =
					data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'No name'
			} else if (field.key === 'name' && type === 'course') {
				// Special handling for course names
				formatted.name = data.name || data.internalName || 'Unnamed Course'
			} else {
				// Default: get value from data
				formatted[field.key] = data[field.key] || ''
			}
		})

		// Add type-specific fields needed for actions (not displayed but used for navigation)
		if (type === 'course') {
			formatted.path = data.path || ''
		}

		return formatted
	}

	/**
	 * Format user data for display (legacy compatibility)
	 * @param {Object} user - User document
	 * @returns {Object}
	 */
	formatUserData(user) {
		return this.formatEntityData(user, 'user')
	}

	/**
	 * Format course data for display (legacy compatibility)
	 * @param {Object} course - Course document
	 * @returns {Object}
	 */
	formatCourseData(course) {
		return this.formatEntityData(course, 'course')
	}

	/**
	 * Register a tool
	 * @param {BaseTool} tool - Tool instance
	 */
	registerTool(tool) {
		this.tools.set(tool.name, tool)
	}

	/**
	 * Get tools metadata for AI
	 * @returns {Array}
	 */
	getToolsMetadata() {
		const metadata = []
		this.tools.forEach((tool) => {
			metadata.push({
				name: tool.name,
				description: tool.description,
				parameters: tool.schema
			})
		})
		return metadata
	}

	/**
	 * Get conversation history formatted for AI
	 * @returns {Array}
	 */
	getConversationHistory() {
		return this.conversationHistory
	}

	/**
	 * Add message to conversation history
	 * @param {string} role - 'user' or 'assistant'
	 * @param {string} content - Message content
	 */
	addToHistory(role, content) {
		this.conversationHistory.push({ role, content })

		// Trim if too long
		if (this.conversationHistory.length > this.maxHistoryLength) {
			this.conversationHistory = this.conversationHistory.slice(-this.maxHistoryLength)
		}
	}

	/**
	 * Clear conversation history
	 */
	clearHistory() {
		this.conversationHistory = []
		this.currentContext = {
			lastUserId: null,
			lastDocumentId: null,
			lastCollection: null
		}
	}

	/**
	 * Get execution context from store
	 * @returns {Object}
	 */
	getContext() {
		const currentUser = store.getters.getCurrentUser
		const tenantId = store.getters.getTenantId

		return {
			userId: currentUser?.uid || null,
			tenantId: tenantId || null,
			user: currentUser || null,
			timestamp: new Date()
		}
	}
}

// Export singleton instance
export default new AIAgentService()
